const CONSENT_KEY = 'noiz-cookie-consent';
const GA4_ID = 'G-3BVFHNHS1T';
const GTM_ID = 'GTM-K8282S7';
const FB_PIXEL_ID = '704017346599522';
const INTERCOM_APP_ID = 'ito0ceux';

let trackingInitialized = false;
let lastTrackedPath = null;

export const getConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch (e) {
    return null;
  }
};

export const setConsent = (value) => {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch (e) {
    // localStorage unavailable
  }
};

export const consentAccepted = () => getConsent() === 'accepted';
export const hasMadeChoice = () => getConsent() !== null;

const appendScriptOnce = ({ src, id, target = 'head' }) => {
  if (!src) return null;
  const byId = id ? document.getElementById(id) : null;
  const bySrc = document.querySelector(`script[src="${src}"]`);
  if (byId || bySrc) return byId || bySrc;

  const script = document.createElement('script');
  if (id) script.id = id;
  script.async = true;
  script.src = src;

  if (target === 'first-script') {
    const first = document.getElementsByTagName('script')[0];
    if (first && first.parentNode) {
      first.parentNode.insertBefore(script, first);
      return script;
    }
  }

  document.head.appendChild(script);
  return script;
};

const loadGA4 = () => {
  appendScriptOnce({
    src: `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`,
    id: `ga4-${GA4_ID}`,
  });

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    // Must use regular function — gtag relies on `arguments` object
    window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  }

  window.gtag('js', new Date());
  window.gtag('config', GA4_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });
};

const loadGTM = () => {
  if (document.getElementById(`gtm-${GTM_ID}`)) return;

  const w = window;
  const d = document;
  const l = 'dataLayer';
  const i = GTM_ID;
  w[l] = w[l] || [];
  w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = d.getElementsByTagName('script')[0];
  const j = d.createElement('script');
  j.id = `gtm-${GTM_ID}`;
  j.async = true;
  j.src = `https://www.googletagmanager.com/gtm.js?id=${i}`;
  f.parentNode.insertBefore(j, f);
};

const loadFacebookPixel = () => {
  if (window.fbq) return;
  const n = function fbqFn(...args) {
    if (n.callMethod) n.callMethod(...args);
    else n.queue.push(args);
  };
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  appendScriptOnce({
    src: 'https://connect.facebook.net/en_US/fbevents.js',
    id: `fb-pixel-${FB_PIXEL_ID}`,
    target: 'first-script',
  });

  window.fbq('init', FB_PIXEL_ID);
};

const loadIntercom = () => {
  appendScriptOnce({
    src: `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`,
    id: `intercom-${INTERCOM_APP_ID}`,
    target: 'first-script',
  });
};

export const initAnalyticsTracking = () => {
  if (trackingInitialized) return;

  loadGA4();
  loadGTM();
  loadFacebookPixel();
  loadIntercom();
  trackingInitialized = true;
};

const toPath = (locationOrPath) => {
  if (!locationOrPath) {
    return `${window.location.pathname || '/'}${window.location.search || ''}`;
  }

  if (typeof locationOrPath === 'string') return locationOrPath;
  const pathname = locationOrPath.pathname || '/';
  const search = locationOrPath.search || '';
  return `${pathname}${search}`;
};

export const trackPageView = (locationOrPath) => {
  // Gate on trackingInitialized as well as consent. history.listen (react-router v3
  // synced history) fires synchronously on subscribe — before bootstrapTracking() has
  // loaded the analytics scripts. Without this guard that early call would record
  // lastTrackedPath and the first real pageview (from Main.componentDidMount) would be
  // de-duplicated away.
  if (!consentAccepted() || !trackingInitialized) return;

  const path = toPath(locationOrPath);
  if (path === lastTrackedPath) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      send_to: GA4_ID,
    });
  }

  if (window.fbq) {
    window.fbq('track', 'PageView');
  }

  lastTrackedPath = path;
};

export const trackingEnabled = () => consentAccepted() && trackingInitialized;

// Call on app bootstrap: initializes tracking if user has already accepted consent.
export const bootstrapTracking = () => {
  if (consentAccepted()) {
    initAnalyticsTracking();
  }
};
